/**
 * COMPANY ACCOUNT MANAGEMENT DASHBOARD
 * 
 * Admin interface for managing company accounts including:
 * - Company account review and approval
 * - Company KYC status management
 * - Company document verification
 * - Officer management overview
 */

import React, { useState, useEffect, useCallback } from 'react';
import { logAdminAction } from '../../lib/adminAuth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
import { sendKYCApprovedEmail, sendKYCRejectedEmail } from '../../lib/services/companyEmailService';
import {createCompanyCredentials} from '../../lib/services/companyAuthService';
import type { CompanyAccount, CompanyKYCStatus, CompanyOfficer, CompanyDocument } from '../../types/company';
import { getSessionHeaders } from '../../lib/serverSession';

interface CompanyManagerProps {
  currentUser?: { email?: string };
}

interface CompanyWithDetails extends CompanyAccount {
  officers?: CompanyOfficer[];
  documents?: CompanyDocument[];
  primaryDirector?: {
    id: number;
    username: string;
    email: string;
    full_name: string;
  };
}

interface CompanyStatistics {
  total: number;
  draft: number;
  pending_documents: number;
  pending_review: number;
  approved: number;
  rejected: number;
  suspended: number;
}

type TabType = 'pending_review' | 'pending_documents' | 'approved' | 'rejected' | 'all';

const kycStatusConfig: Record<CompanyKYCStatus, { label: string; color: string; bgColor: string; icon: string }> = {
  draft: { label: 'Draft', color: 'text-white', bgColor: 'bg-gray-700', icon: '📝' },
  pending_documents: { label: 'Pending Documents', color: 'text-yellow-400', bgColor: 'bg-yellow-900/30', icon: '📄' },
  pending_review: { label: 'Pending Review', color: 'text-blue-400', bgColor: 'bg-blue-900/30', icon: '🔍' },
  additional_info_required: { label: 'Info Required', color: 'text-orange-400', bgColor: 'bg-orange-900/30', icon: '⚠️' },
  approved: { label: 'Verified', color: 'text-green-400', bgColor: 'bg-green-900/30', icon: '✅' },
  rejected: { label: 'Rejected', color: 'text-red-400', bgColor: 'bg-red-900/30', icon: '❌' },
  suspended: { label: 'Suspended', color: 'text-red-400', bgColor: 'bg-red-900/30', icon: '🚫' },
  expired: { label: 'Expired', color: 'text-orange-400', bgColor: 'bg-orange-900/30', icon: '⏰' }
};

export const CompanyManager: React.FC<CompanyManagerProps> = ({ currentUser }) => {
  const [companies, setCompanies] = useState<CompanyWithDetails[]>([]);
  const [statistics, setStatistics] = useState<CompanyStatistics>({
    total: 0, draft: 0, pending_documents: 0, pending_review: 0, approved: 0, rejected: 0, suspended: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('pending_review');
  const [selectedCompany, setSelectedCompany] = useState<CompanyWithDetails | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [processing, setProcessing] = useState(false);
  const [adminComments, setAdminComments] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [activationUsername, setActivationUsername] = useState('');
  const [activationPassword, setActivationPassword] = useState('');

  // Load companies data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load statistics via API
      const statsResponse = await fetch(`${API_BASE_URL}/api/admin/company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ operation: 'load_statistics' })
      });
      const statsResult = await statsResponse.json();
      if (statsResponse.ok && statsResult.data) {
        setStatistics(statsResult.data);
      }

      // Load companies via API
      const companiesResponse = await fetch(`${API_BASE_URL}/api/admin/company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'load_companies',
          data: { activeTab, searchTerm, limit: 100 }
        })
      });
      const companiesResult = await companiesResponse.json();
      if (!companiesResponse.ok) throw new Error(companiesResult.error);
      const data = companiesResult.data;

      // Map to CompanyWithDetails
      const mappedCompanies: CompanyWithDetails[] = (data || []).map((c: any) => ({
        id: c.id,
        primaryDirectorUserId: c.primary_director_user_id,
        legalName: c.legal_name,
        tradingName: c.trading_name,
        registrationNumber: c.registration_number,
        taxIdentificationNumber: c.tax_identification_number,
        jurisdictionCountryCode: c.jurisdiction_country_code,
        companyType: c.company_type,
        dateOfIncorporation: c.date_of_incorporation,
        industryClassification: c.industry_classification,
        industryName: c.industry_name,
        businessDescription: c.business_description,
        registeredAddress: {
          street: c.registered_address_street,
          city: c.registered_address_city,
          province: c.registered_address_province,
          postalCode: c.registered_address_postal_code,
          countryCode: c.registered_address_country_code,
        },
        businessAddress: c.business_address_street ? {
          street: c.business_address_street,
          city: c.business_address_city,
          province: c.business_address_province,
          postalCode: c.business_address_postal_code,
          countryCode: c.business_address_country_code,
        } : undefined,
        primaryEmail: c.primary_email,
        primaryPhone: c.primary_phone,
        websiteUrl: c.website_url,
        logoUrl: c.logo_url,
        kycStatus: c.kyc_status,
        kycSubmittedAt: c.kyc_submitted_at,
        kycReviewedAt: c.kyc_reviewed_at,
        kycReviewedBy: c.kyc_reviewed_by,
        kycRejectionReason: c.kyc_rejection_reason,
        isPepAssociated: c.is_pep_associated || false,
        isSanctionsChecked: c.is_sanctions_checked || false,
        riskLevel: c.risk_level || 'standard',
        isActive: c.is_active,
        isFrozen: c.is_frozen || false,
        createdAt: c.created_at,
        updatedAt: c.updated_at,
      }));

      setCompanies(mappedCompanies);
    } catch (err) {
      console.error('Error loading company data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load company data');
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchTerm]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load company details with officers and documents
  const loadCompanyDetails = async (company: CompanyWithDetails) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'load_company_details',
          data: { companyId: company.id, primaryDirectorUserId: company.primaryDirectorUserId }
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      const { officers, documents, primaryDirector } = result.data;

      setSelectedCompany({
        ...company,
        officers: officers || [],
        documents: documents || [],
        primaryDirector: primaryDirector ? {
          id: primaryDirector.id,
          username: primaryDirector.username,
          email: primaryDirector.email,
          full_name: primaryDirector.full_name || primaryDirector.username,
        } : undefined,
      });
    } catch (err) {
      console.error('Error loading company details:', err);
    }
  };

  // Approve company KYC
  const handleApprove = async () => {
    if (!selectedCompany || !currentUser?.email) return;

    try {
      setProcessing(true);

      const response = await fetch(`${API_BASE_URL}/api/admin/company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'update_kyc_status',
          data: {
            companyId: selectedCompany.id,
            status: 'approved',
            reviewedBy: currentUser.email,
            adminComments
          }
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      await logAdminAction(currentUser.email, 'COMPANY_KYC_APPROVED', 'company_accounts', selectedCompany.id, {
        company_name: selectedCompany.legalName,
        registration_number: selectedCompany.registrationNumber,
        comments: adminComments,
      });

      // Send KYC approved email notification
      sendKYCApprovedEmail({
        companyName: selectedCompany.legalName,
        registrationNumber: selectedCompany.registrationNumber || '',
        directorName: selectedCompany.primaryDirector?.full_name || 'Director',
        directorEmail: selectedCompany.primaryDirector?.email || selectedCompany.primaryEmail || ''
      }).catch(err => console.error('Failed to send KYC approved email:', err));

      alert('Company KYC approved successfully!');
      setSelectedCompany(null);
      setAdminComments('');
      loadData();
    } catch (err) {
      console.error('Error approving company:', err);
      alert('Failed to approve company KYC');
    } finally {
      setProcessing(false);
    }
  };

  // Reject company KYC
  const handleReject = async () => {
    if (!selectedCompany || !currentUser?.email || !rejectionReason.trim()) return;

    try {
      setProcessing(true);

      const response = await fetch(`${API_BASE_URL}/api/admin/company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'update_kyc_status',
          data: {
            companyId: selectedCompany.id,
            status: 'rejected',
            reviewedBy: currentUser.email,
            rejectionReason
          }
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      await logAdminAction(currentUser.email, 'COMPANY_KYC_REJECTED', 'company_accounts', selectedCompany.id, {
        company_name: selectedCompany.legalName,
        registration_number: selectedCompany.registrationNumber,
        rejection_reason: rejectionReason,
      });

      // Send KYC rejected email notification
      sendKYCRejectedEmail({
        companyName: selectedCompany.legalName,
        registrationNumber: selectedCompany.registrationNumber || '',
        directorName: selectedCompany.primaryDirector?.full_name || 'Director',
        directorEmail: selectedCompany.primaryDirector?.email || selectedCompany.primaryEmail || '',
        rejectionReason: rejectionReason
      }).catch(err => console.error('Failed to send KYC rejected email:', err));

      alert('Company KYC rejected.');
      setSelectedCompany(null);
      setRejectionReason('');
      setShowRejectModal(false);
      loadData();
    } catch (err) {
      console.error('Error rejecting company:', err);
      alert('Failed to reject company KYC');
    } finally {
      setProcessing(false);
    }
  };

  // Request additional documents
  const handleRequestDocuments = async () => {
    if (!selectedCompany || !currentUser?.email) return;

    try {
      setProcessing(true);

      const response = await fetch(`${API_BASE_URL}/api/admin/company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'update_kyc_status',
          data: {
            companyId: selectedCompany.id,
            status: 'additional_info_required',
            reviewedBy: currentUser.email,
            adminComments
          }
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);

      await logAdminAction(currentUser.email, 'COMPANY_ADDITIONAL_INFO_REQUESTED', 'company_accounts', selectedCompany.id, {
        company_name: selectedCompany.legalName,
        comments: adminComments,
      });

      alert('Additional documents requested.');
      setSelectedCompany(null);
      setAdminComments('');
      loadData();
    } catch (err) {
      console.error('Error requesting documents:', err);
      alert('Failed to request additional documents');
    } finally {
      setProcessing(false);
    }
  };

  // Activate company account (create login credentials)
  const handleActivateAccount = async () => {
    if (!selectedCompany || !currentUser?.email || !activationUsername || !activationPassword) return;

    try {
      setProcessing(true);

      // Use the company auth service to create credentials
      const result = await createCompanyCredentials({
        companyId: selectedCompany.id,
        username: activationUsername,
        password: activationPassword,
        setupByUserId: selectedCompany.primaryDirectorUserId
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to create credentials');
      }

      await logAdminAction(currentUser.email, 'COMPANY_ACCOUNT_ACTIVATED', 'company_accounts', selectedCompany.id, {
        company_name: selectedCompany.legalName,
        username: activationUsername,
      });

      alert(`Company account activated successfully!\n\nUsername: ${activationUsername}\n\nPlease share these credentials with the primary director securely.`);
      setShowActivateModal(false);
      setActivationUsername('');
      setActivationPassword('');
      loadData();
    } catch (err: any) {
      console.error('Error activating company:', err);
      alert('Failed to activate company account: ' + (err.message || 'Unknown error'));
    } finally {
      setProcessing(false);
    }
  };

  // Generate random password
  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 16; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setActivationPassword(password);
  };

  // Format date helper
  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  if (loading && companies.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-yellow-400 border-t-transparent rounded-full"></div>
        <span className="ml-3 text-white">Loading companies...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Company Account Management</h2>
          <p className="text-white mt-1">Review and manage company KYC applications</p>
        </div>
        <button onClick={loadData} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">
          🔄 Refresh
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {[
          { key: 'total', label: 'Total', value: statistics.total, color: 'blue', icon: '🏢' },
          { key: 'pending_review', label: 'Pending Review', value: statistics.pending_review, color: 'yellow', icon: '🔍' },
          { key: 'pending_documents', label: 'Pending Docs', value: statistics.pending_documents, color: 'orange', icon: '📄' },
          { key: 'draft', label: 'Draft', value: statistics.draft, color: 'gray', icon: '📝' },
          { key: 'approved', label: 'Approved', value: statistics.approved, color: 'green', icon: '✅' },
          { key: 'rejected', label: 'Rejected', value: statistics.rejected, color: 'red', icon: '❌' },
          { key: 'suspended', label: 'Suspended', value: statistics.suspended, color: 'red', icon: '🚫' },
        ].map(stat => (
          <div key={stat.key} className={`glass-card p-4 border-l-4 border-${stat.color}-500`}>
            <div className="text-2xl mb-1">{stat.icon}</div>
            <div className={`text-2xl font-bold text-${stat.color}-400`}>{stat.value}</div>
            <div className="text-sm text-white">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs and Search */}
      <div className="glass-card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-4 border-b border-gray-700">
          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'pending_review' as TabType, label: 'Pending Review', count: statistics.pending_review },
              { key: 'pending_documents' as TabType, label: 'Pending Docs', count: statistics.pending_documents },
              { key: 'approved' as TabType, label: 'Approved', count: statistics.approved },
              { key: 'rejected' as TabType, label: 'Rejected', count: statistics.rejected },
              { key: 'all' as TabType, label: 'All', count: statistics.total },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'bg-yellow-500 text-black'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search by name, registration, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full md:w-80 px-4 py-2 pl-10 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-yellow-500 focus:outline-none"
            />
            <span className="absolute left-3 top-2.5 text-white">🔍</span>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="p-4 bg-red-900/30 border-l-4 border-red-500 text-red-400">
            {error}
          </div>
        )}

        {/* Company List */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="px-4 py-3 text-left text-sm font-medium text-white">Company</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-white">Registration</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-white">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-white">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-white">Submitted</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-white">
                    No companies found matching the current filter.
                  </td>
                </tr>
              ) : (
                companies.map(company => {
                  const statusConfig = kycStatusConfig[company.kycStatus];
                  return (
                    <tr key={company.id} className="border-b border-gray-800 hover:bg-gray-800/50 cursor-pointer" onClick={() => loadCompanyDetails(company)}>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          {company.logoUrl ? (
                            <img src={company.logoUrl} alt="" className="w-10 h-10 rounded-lg object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center text-white">🏢</div>
                          )}
                          <div>
                            <div className="font-medium text-white">{company.legalName}</div>
                            {company.tradingName && <div className="text-sm text-white">t/a {company.tradingName}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-gray-300">{company.registrationNumber || 'N/A'}</td>
                      <td className="px-4 py-4 text-gray-300">{company.companyType || 'N/A'}</td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${statusConfig.bgColor} ${statusConfig.color}`}>
                          {statusConfig.icon} {statusConfig.label}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-gray-300">{formatDate(company.kycSubmittedAt)}</td>
                      <td className="px-4 py-4 text-right">
                        <button
                          onClick={(e) => { e.stopPropagation(); loadCompanyDetails(company); }}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded text-sm transition-colors"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Company Detail Modal */}
      {selectedCompany && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-gray-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gray-900 px-6 py-4 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {selectedCompany.logoUrl ? (
                  <img src={selectedCompany.logoUrl} alt="" className="w-12 h-12 rounded-lg object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center text-2xl">🏢</div>
                )}
                <div>
                  <h3 className="text-xl font-bold text-white">{selectedCompany.legalName}</h3>
                  {selectedCompany.tradingName && <p className="text-sm text-white">Trading as: {selectedCompany.tradingName}</p>}
                </div>
              </div>
              <button onClick={() => setSelectedCompany(null)} className="text-gray-400 hover:text-white text-2xl">×</button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Status & Primary Director */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="glass-card p-4">
                  <h4 className="text-sm font-medium text-white mb-2">KYC Status</h4>
                  <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${kycStatusConfig[selectedCompany.kycStatus].bgColor} ${kycStatusConfig[selectedCompany.kycStatus].color}`}>
                    {kycStatusConfig[selectedCompany.kycStatus].icon} {kycStatusConfig[selectedCompany.kycStatus].label}
                  </span>
                  {selectedCompany.kycRejectionReason && (
                    <p className="mt-2 text-sm text-red-400">Reason: {selectedCompany.kycRejectionReason}</p>
                  )}
                </div>
                <div className="glass-card p-4">
                  <h4 className="text-sm font-medium text-white mb-2">Primary Director</h4>
                  {selectedCompany.primaryDirector ? (
                    <div>
                      <p className="text-white font-medium">{selectedCompany.primaryDirector.full_name}</p>
                      <p className="text-sm text-white">{selectedCompany.primaryDirector.email}</p>
                    </div>
                  ) : (
                    <p className="text-white">Not available</p>
                  )}
                </div>
              </div>

              {/* Company Details Grid */}
              <div className="glass-card p-4">
                <h4 className="text-sm font-medium text-white mb-4">Company Information</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div><span className="text-white">Registration:</span> <span className="text-white ml-1">{selectedCompany.registrationNumber || 'N/A'}</span></div>
                  <div><span className="text-white">Tax Number:</span> <span className="text-white ml-1">{selectedCompany.taxIdentificationNumber || 'N/A'}</span></div>
                  <div><span className="text-white">Company Type:</span> <span className="text-white ml-1">{selectedCompany.companyType || 'N/A'}</span></div>
                  <div><span className="text-white">Incorporation:</span> <span className="text-white ml-1">{formatDate(selectedCompany.dateOfIncorporation)}</span></div>
                  <div><span className="text-white">Industry:</span> <span className="text-white ml-1">{selectedCompany.industryName || 'N/A'}</span></div>
                  <div><span className="text-white">Jurisdiction:</span> <span className="text-white ml-1">{selectedCompany.jurisdictionCountryCode || 'N/A'}</span></div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="glass-card p-4">
                <h4 className="text-sm font-medium text-white mb-4">Contact Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div><span className="text-white">Email:</span> <span className="text-white ml-1">{selectedCompany.primaryEmail || 'N/A'}</span></div>
                  <div><span className="text-white">Phone:</span> <span className="text-white ml-1">{selectedCompany.primaryPhone || 'N/A'}</span></div>
                  <div><span className="text-white">Website:</span> <span className="text-white ml-1">{selectedCompany.websiteUrl || 'N/A'}</span></div>
                </div>
              </div>

              {/* Registered Address */}
              {selectedCompany.registeredAddress && (
                <div className="glass-card p-4">
                  <h4 className="text-sm font-medium text-white mb-4">Registered Address</h4>
                  <p className="text-white">
                    {selectedCompany.registeredAddress.street}, {selectedCompany.registeredAddress.city}, {selectedCompany.registeredAddress.province} {selectedCompany.registeredAddress.postalCode}, {selectedCompany.registeredAddress.countryCode}
                  </p>
                </div>
              )}

              {/* Officers Section */}
              <div className="glass-card p-4">
                <h4 className="text-sm font-medium text-white mb-4">Officers ({selectedCompany.officers?.length || 0})</h4>
                {selectedCompany.officers && selectedCompany.officers.length > 0 ? (
                  <div className="space-y-3">
                    {selectedCompany.officers.map((officer: any) => (
                      <div key={officer.id} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                        <div>
                          <p className="text-white font-medium">{officer.first_name} {officer.last_name} {officer.user_id && '(Linked)'}</p>
                          <p className="text-sm text-white">{officer.custom_role_title || officer.role}</p>
                        </div>
                        <div className="text-right text-sm">
                          {officer.shareholding_percentage && <p className="text-yellow-400">{officer.shareholding_percentage}% shares</p>}
                          {officer.kyc_verified && <span className="text-green-400">✓ KYC Verified</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white">No officers on record</p>
                )}
              </div>

              {/* Documents Section */}
              <div className="glass-card p-4">
                <h4 className="text-sm font-medium text-white mb-4">Documents ({selectedCompany.documents?.length || 0})</h4>
                {selectedCompany.documents && selectedCompany.documents.length > 0 ? (
                  <div className="space-y-2">
                    {selectedCompany.documents.map((doc: any) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">📄</span>
                          <div>
                            <p className="text-white">{doc.document_name || doc.document_type}</p>
                            <p className="text-sm text-white">{doc.file_name}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-sm ${
                          doc.verification_status === 'verified' ? 'bg-green-900/30 text-green-400' :
                          doc.verification_status === 'rejected' ? 'bg-red-900/30 text-red-400' :
                          'bg-yellow-900/30 text-yellow-400'
                        }`}>
                          {doc.verification_status || 'pending'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white">No documents uploaded</p>
                )}
              </div>

              {/* Admin Comments */}
              <div className="glass-card p-4">
                <h4 className="text-sm font-medium text-white mb-2">Admin Comments</h4>
                <textarea
                  value={adminComments}
                  onChange={(e) => setAdminComments(e.target.value)}
                  placeholder="Add notes or comments about this company..."
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-yellow-500 focus:outline-none resize-none"
                  rows={3}
                />
              </div>

              {/* Action Buttons */}
              {(selectedCompany.kycStatus === 'pending_review' || selectedCompany.kycStatus === 'pending_documents') && (
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleApprove}
                    disabled={processing}
                    className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-500 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    {processing ? '⏳ Processing...' : '✅ Approve KYC'}
                  </button>
                  <button
                    onClick={handleRequestDocuments}
                    disabled={processing}
                    className="flex-1 px-6 py-3 bg-orange-600 hover:bg-orange-500 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    📄 Request Documents
                  </button>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    disabled={processing}
                    className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-500 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    ❌ Reject KYC
                  </button>
                </div>
              )}

              {/* Approved Company Actions */}
              {selectedCompany.kycStatus === 'approved' && (
                <div className="space-y-4">
                  <div className="bg-green-900/30 border border-green-600 rounded-lg p-4">
                    <p className="text-green-400 flex items-center gap-2">
                      <span>✅</span>
                      This company's KYC is approved. You can now activate their account.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => {
                        // Generate default username from company name
                        const defaultUsername = selectedCompany.legalName
                          .toLowerCase()
                          .replace(/[^a-z0-9]/g, '_')
                          .replace(/_+/g, '_')
                          .slice(0, 30);
                        setActivationUsername(defaultUsername);
                        setShowActivateModal(true);
                      }}
                      disabled={processing}
                      className="flex-1 px-6 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      🔑 Activate Company Account
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[60]">
          <div className="bg-gray-900 rounded-xl max-w-md w-full p-6 border border-gray-700">
            <h3 className="text-xl font-bold text-white mb-4">Reject Company KYC</h3>
            <p className="text-white mb-4">Please provide a reason for rejecting this company's KYC application.</p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-red-500 focus:outline-none resize-none mb-4"
              rows={4}
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setShowRejectModal(false); setRejectionReason(''); }}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectionReason.trim() || processing}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {processing ? '⏳ Processing...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activate Account Modal */}
      {showActivateModal && selectedCompany && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[60]">
          <div className="bg-gray-900 rounded-xl max-w-md w-full p-6 border border-gray-700">
            <h3 className="text-xl font-bold text-white mb-4">🔑 Activate Company Account</h3>
            <p className="text-white mb-4">
              Create login credentials for <strong className="text-white">{selectedCompany.legalName}</strong>
            </p>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-white mb-2">Username</label>
                <input
                  type="text"
                  value={activationUsername}
                  onChange={(e) => setActivationUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="company_username"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-yellow-500 focus:outline-none"
                />
                <p className="text-sm text-gray-500 mt-1">4-50 characters, lowercase letters, numbers, and underscores only</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Password</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={activationPassword}
                    onChange={(e) => setActivationPassword(e.target.value)}
                    placeholder="Enter or generate password"
                    className="flex-1 px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-yellow-500 focus:outline-none font-mono"
                  />
                  <button
                    type="button"
                    onClick={generateRandomPassword}
                    className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                    title="Generate random password"
                  >
                    🎲
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">Min 8 chars, must include: uppercase, lowercase, number, special character</p>
              </div>
            </div>

            <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-3 mb-4">
              <p className="text-yellow-400 text-sm">
                ⚠️ Please save these credentials securely and share them with the primary director.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowActivateModal(false);
                  setActivationUsername('');
                  setActivationPassword('');
                }}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleActivateAccount}
                disabled={!activationUsername || activationUsername.length < 4 || !activationPassword || activationPassword.length < 8 || processing}
                className="flex-1 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {processing ? '⏳ Activating...' : '✅ Activate Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyManager;
